// ============================================================================
// NewMeetingDialog — wizard « Nouvelle rencontre » (E3b, F-433)
// ============================================================================
// Champs : titre, datetime-local (Montreal), duree (defaut 90), lieu.
// Succes = drill-in /rencontres/{id}. Visible ADMIN (viewer.canCreate).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { useCreateMeeting, meetingErrorCode } from '../../hooks/useMeetings';

export function NewMeetingDialog({
  open,
  onClose,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateMeeting();

  const [title, setTitle] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(90);
  const [location, setLocation] = useState('');
  const [stepError, setStepError] = useState<string | null>(null);

  if (!open) return null;

  function reset() {
    setTitle('');
    setScheduledAt('');
    setDurationMinutes(90);
    setLocation('');
    setStepError(null);
  }

  function close() {
    reset();
    onClose();
  }

  function submit() {
    if (!title.trim()) {
      setStepError(t('gest:rencontres.wizard.titleRequired'));
      return;
    }
    if (!scheduledAt) {
      setStepError(t('gest:rencontres.wizard.scheduledRequired'));
      return;
    }
    setStepError(null);
    create.mutate(
      {
        title: title.trim(),
        scheduledAt,
        durationMinutes,
        location: location.trim() ? location.trim() : null,
        status: 'upcoming',
      },
      {
        onSuccess: ({ id }) => {
          close();
          navigate(`/rencontres/${id}`);
        },
        onError: (err) => {
          close();
          onError(meetingErrorCode(err));
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('gest:rencontres.wizard.title')}
    >
      <div className="flex max-h-full w-full max-w-md flex-col gap-4 overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-md dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-body font-semibold text-slate-900 dark:text-slate-100">
            {t('gest:rencontres.wizard.title')}
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label={t('gest:rencontres.wizard.cancel')}
            className="touch-manipulation rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {stepError ? (
          <p role="alert" className="text-field text-red-700 dark:text-red-300">{stepError}</p>
        ) : null}

        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-slate-700 dark:text-slate-300">
            {t('gest:rencontres.wizard.titleLabel')}
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className={GS_COMPONENTS.input}
            autoFocus
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-slate-700 dark:text-slate-300">
            {t('gest:rencontres.wizard.scheduledLabel')}
          </span>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className={GS_COMPONENTS.input}
          />
          <span className="text-meta text-slate-500 dark:text-slate-400">
            {t('gest:rencontres.wizard.tzHint')}
          </span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-slate-700 dark:text-slate-300">
            {t('gest:rencontres.wizard.durationLabel')}
          </span>
          <input
            type="number"
            min={0}
            max={1440}
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Number(e.target.value) || 0)}
            className={GS_COMPONENTS.input}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-slate-700 dark:text-slate-300">
            {t('gest:rencontres.wizard.locationLabel')}
          </span>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={300}
            className={GS_COMPONENTS.input}
            placeholder={t('gest:rencontres.wizard.locationPlaceholder')}
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={close} className={GS_COMPONENTS.btnSecondary}>
            {t('gest:rencontres.wizard.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={create.isPending}
            className={GS_COMPONENTS.btnAction}
          >
            {t('gest:rencontres.wizard.create')}
          </button>
        </div>
      </div>
    </div>
  );
}
